import { useState } from 'react';
import { XAxis, YAxis, CartesianGrid, Tooltip, ComposedChart, Legend, Line, Brush, Bar, ResponsiveContainer } from 'recharts';
import { Tooltip as CustomTooltip } from "../../../Chart/Tooltip";
import { useChart } from './useChart';
import { useTheme } from '@mui/material';
import { ChartLegend } from '../../../Chart/ChartLegend';

export const BarChart = ({ 
  data,
  components,
  unit = 'kWh', 
  groupTitle = "",
  enableLegendTooltip=true,
  onBrushChange
}) => {
  const theme = useTheme();
  const [hover, setHover] = useState(null);

  const handleLegendMouseEnter = (dataKey) => {
    setHover(dataKey);
  };

  const handleLegendMouseLeave = (e) => {
    setHover(null);
  };

  const {colors, adaptedData, hiddenFields, toggleBarVisibility} = useChart( data );

  const getPayload = () => {
    let payload = []
    data?.magnitudes?.elements.map((el, idx) => {
      payload.push({
        'value': el,
        'color': colors[idx]
      })
    })
    payload.push({
      'value': 'total',
      'color': 'black'
    })
    return payload;
  }

  const getFields = () => {
    const fields = {
      ...Object.fromEntries(
        data?.magnitudes?.elements.map(el => [el, true])
      ),
      total: true
    }

    Object.keys(hiddenFields).forEach(key => {
      if (fields.hasOwnProperty(key)) {
        fields[key] = !hiddenFields[key]
      }
    })

    return fields
  }

  const renderLegend = () => {
    return (
      <ChartLegend
        payload={getPayload()}
        components={components}
        toggleBarVisibility={toggleBarVisibility}
        handleLegendMouseEnter={handleLegendMouseEnter}
        handleLegendMouseLeave={handleLegendMouseLeave}
        hover={hover}
        fields={getFields()}
        enableTooltip={enableLegendTooltip}
      />
    )
  }

  const createBars = () => {
    return data.magnitudes.elements.map((el, idx) => {
      return <Bar
        legendType="square"
        unit={unit}
        isAnimationActive={true}
        key={idx}
        type='monotone'
        dataKey={el}
        stackId="activa"
        stroke={"#808080"}
        strokeWidth={hover === el ? 1 : 0}
        fill={colors[idx]}
        hide={hiddenFields[el] === true}
        fillOpacity={(hover === el || !hover) ? 1 : 0.5}
      />
    });
  }

  const createTotalLine = () => {
    return <Line
      name={"Total"}
      unit={unit}
      isAnimationActive={true}
      key={"line"}
      type='monotone'
      dataKey={'total'}
      stroke={"black"}
      fill={"white"}
      hide={hiddenFields.total === true}
      strokeOpacity={(hover === 'total' || !hover) ? 1 : 0.5}
      dot={{ opacity: ((hover === 'total' || !hover) ? 1 : 0.5), strokeOpacity: ((hover === 'total' || !hover) ? 1 : 0.5) }}
    />;
  }

  const handleBrushChange = (brushData) => {
    if (onBrushChange && brushData && adaptedData) {
      const startIndex = brushData.startIndex;
      const endIndex = brushData.endIndex;

      if (startIndex !== undefined && endIndex !== undefined) {
        const startValue = adaptedData[startIndex]?.name;
        const endValue = adaptedData[endIndex]?.name;

        if (startValue !== undefined && endValue !== undefined) {
          onBrushChange(startValue, endValue);
        }
      }
    }
  };
  return (
    <ResponsiveContainer height={500}>
      <ComposedChart
        data={adaptedData}
        margin={{ top: 20, left: 20 }}
        stackOffset="sign"
      >
        <XAxis dataKey="name" angle={-45} height={80} dy={10} textAnchor="end" label={{ value: groupTitle, fill: '#666666', paddingTop: 20 }} />
        <YAxis label={{ value: unit, angle: unit.length > 1 ? -90 : 0, position: 'insideLeft', fill: '#666666' }} />
        <CartesianGrid strokeDasharray="3 3"/>
        {createBars()}
        {createTotalLine()}
        
        <Legend content={renderLegend} />

        <Tooltip content={<CustomTooltip groupTitle={groupTitle} />} />
        <Brush 
          dataKey="name" 
          height={30} 
          stroke={theme.palette.primary.main} 
          onChange={handleBrushChange}
        />
      </ComposedChart>
    </ResponsiveContainer>
  );
}